import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { OptiBaySessionTestPath } from '@/types/optibay';

const sb = supabase as any;

export function useSessionTestPaths(sessionId: string | undefined) {
  return useQuery({
    queryKey: ['optibay-session-test-paths', sessionId],
    queryFn: async (): Promise<OptiBaySessionTestPath[]> => {
      if (!sessionId) return [];
      const { data, error } = await sb
        .from('optibay_session_test_paths')
        .select('*')
        .eq('session_id', sessionId)
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return (data ?? []) as OptiBaySessionTestPath[];
    },
    enabled: !!sessionId,
  });
}

export function useAddSessionTestPath() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      sessionId: string;
      testPathId: string;
      sortOrder?: number;
    }) => {
      const { data, error } = await sb
        .from('optibay_session_test_paths')
        .insert({
          session_id: params.sessionId,
          test_path_id: params.testPathId,
          sort_order: params.sortOrder ?? 0,
        })
        .select()
        .single();
      if (error) throw error;
      return data as OptiBaySessionTestPath;
    },
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ['optibay-session-test-paths', data.session_id] });
    },
  });
}

export function useBulkSetSessionTestPaths() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      sessionId: string;
      testPathIds: string[];
    }) => {
      // Remove existing
      await sb
        .from('optibay_session_test_paths')
        .delete()
        .eq('session_id', params.sessionId);
      // Insert new
      if (params.testPathIds.length === 0) return [];
      const rows = params.testPathIds.map((id, i) => ({
        session_id: params.sessionId,
        test_path_id: id,
        sort_order: i,
      }));
      const { data, error } = await sb
        .from('optibay_session_test_paths')
        .insert(rows)
        .select();
      if (error) throw error;
      return (data ?? []) as OptiBaySessionTestPath[];
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['optibay-session-test-paths', vars.sessionId] });
    },
  });
}

export function useUpdateSessionTestPath() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, updates }: {
      id: string;
      updates: Partial<Pick<OptiBaySessionTestPath, 'status' | 'started_at' | 'completed_at'>>;
    }) => {
      const { data, error } = await sb
        .from('optibay_session_test_paths')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data as OptiBaySessionTestPath;
    },
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ['optibay-session-test-paths', data.session_id] });
    },
  });
}

export function useRemoveSessionTestPath() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, sessionId }: { id: string; sessionId: string }) => {
      const { error } = await sb
        .from('optibay_session_test_paths')
        .delete()
        .eq('id', id);
      if (error) throw error;
      return { sessionId };
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['optibay-session-test-paths', result.sessionId] });
    },
  });
}
